import { useRef, type KeyboardEvent } from 'react'
import { cn } from '@/lib/cn.ts'

export interface TabItem {
  id: string
  label: string
  /** Shown as a pill on the tab, e.g. the number of unsaved changes inside it. */
  badge?: number
}

export interface TabsProps {
  items: TabItem[]
  activeId: string
  onChange: (id: string) => void
  label: string
}

/**
 * Material 3 primary tabs: a label row with a rounded indicator under the
 * active tab.
 *
 * Arrow keys move between tabs because a `tablist` is a single tab stop —
 * without it, keyboard users could focus the row but never reach the other
 * tabs. The row scrolls horizontally rather than wrapping, so a narrow screen
 * keeps one clean line instead of a ragged block.
 */
export function Tabs({ items, activeId, onChange, label }: TabsProps) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({})

  function focusTab(id: string) {
    onChange(id)
    refs.current[id]?.focus()
    refs.current[id]?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const index = items.findIndex((item) => item.id === activeId)

    if (index < 0) return

    const moves: Record<string, number | undefined> = {
      ArrowLeft: index - 1,
      ArrowRight: index + 1,
      Home: 0,
      End: items.length - 1,
    }

    const next = moves[event.key]

    if (next === undefined) return

    event.preventDefault()

    const target = items[(next + items.length) % items.length]

    if (target) focusTab(target.id)
  }

  return (
    <div
      role="tablist"
      aria-label={label}
      onKeyDown={handleKeyDown}
      className="-mx-1 flex gap-1 overflow-x-auto border-b border-outline-variant px-1"
    >
      {items.map((item) => {
        const active = item.id === activeId

        return (
          <button
            key={item.id}
            ref={(node) => {
              refs.current[item.id] = node
            }}
            type="button"
            role="tab"
            id={`tab-${item.id}`}
            aria-selected={active}
            aria-controls={`panel-${item.id}`}
            tabIndex={active ? 0 : -1}
            onClick={() => focusTab(item.id)}
            className={cn(
              'relative shrink-0 rounded-t-sm px-4 py-3.5 text-label-md font-medium',
              'transition-colors duration-200 ease-emphasized',
              active
                ? 'text-primary'
                : 'text-on-surface-variant hover:bg-primary/5 hover:text-on-surface',
            )}
          >
            <span className="flex items-center gap-2">
              {item.label}
              {item.badge ? (
                <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-label-sm font-medium text-on-primary">
                  {item.badge}
                </span>
              ) : null}
            </span>

            {active ? (
              <span
                aria-hidden="true"
                className="absolute inset-x-3 bottom-0 h-[3px] rounded-t-full bg-primary"
              />
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

export interface TabPanelProps {
  id: string
  children: React.ReactNode
}

export function TabPanel({ id, children }: TabPanelProps) {
  return (
    <div role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`} tabIndex={0}>
      {children}
    </div>
  )
}
